import { useState } from 'react'
import { Crest } from '@/components/Crest'
import { supabase } from '@/lib/supabase'
import { useAuth } from './AuthProvider'

/** Shortest password worth calling a password. Supabase enforces its own
 *  minimum too; this is the one the person actually sees. */
const MIN = 12

/**
 * Where an invitation link and a reset link both land.
 *
 * The person choosing the password is the only one who ever knows it. Nobody
 * sets a password on somebody's behalf, which is why users-add.ts sends an
 * invitation rather than creating a credential: an administrator-chosen
 * password is one that has travelled through a terminal or a text message and
 * is known to two people.
 *
 * The guidance is three words long on purpose. Rules about symbols and digits
 * produce Password1! and a Post-it on the monitor; length produces something
 * nobody else guesses.
 */
export function SetPassword() {
  const { session, clearPasswordPrompt, signOut } = useAuth()
  const [password, setPassword] = useState('')
  const [again, setAgain] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const tooShort = password.length > 0 && password.length < MIN
  const mismatch = again.length > 0 && again !== password
  const ready = password.length >= MIN && again === password

  async function save(event: React.FormEvent) {
    event.preventDefault()
    if (!ready) return
    setBusy(true)
    setError(null)
    const { error: err } = await supabase.auth.updateUser({ password })
    setBusy(false)
    if (err) {
      setError(
        /weak|pwned|compromis|leaked/i.test(err.message)
          ? 'That password has appeared in a known data breach, so it is not safe to use here. Pick something else.'
          : err.message,
      )
      return
    }
    clearPasswordPrompt()
  }

  return (
    <div className="signin">
      <div className="card">
        <div className="crest">
          <Crest width={150} variant="lockup" />
          <div className="wordmark" style={{ marginTop: 4 }}>
            HR &middot; People &amp; Compliance
          </div>
        </div>
        <div className="body">
          <p className="lede">Choose a password</p>
          <p className="note">
            Signed in as {session?.user.email ?? 'your account'}. Only you will know this password.
          </p>

          <form onSubmit={save} style={{ marginTop: 14 }}>
            <label className="stackfield">
              <span>New password</span>
              <input
                type="password"
                required
                autoFocus
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            <label className="stackfield">
              <span>Type it again</span>
              <input
                type="password"
                required
                autoComplete="new-password"
                value={again}
                onChange={(e) => setAgain(e.target.value)}
              />
            </label>

            <p className="note">
              At least {MIN} characters. Three unrelated words are easier to remember and harder to
              guess than a short one with symbols in it.
            </p>
            {tooShort && <p className="note">That is {MIN - password.length} character(s) short.</p>}
            {mismatch && <p className="note">The two do not match yet.</p>}

            <div className="actions">
              <button className="btn primary" type="submit" disabled={busy || !ready}>
                {busy ? 'Saving…' : 'Save and continue'}
              </button>
              <button className="btn quiet" type="button" onClick={() => void signOut()}>
                Sign out instead
              </button>
            </div>
          </form>

          {error !== null && (
            <p style={{ fontSize: 13, color: 'var(--sev-critical)', margin: '14px 0 0' }}>{error}</p>
          )}
        </div>
      </div>
    </div>
  )
}
